import { Component, Inject, Input } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ModalService } from '../../../../shared/services/modal/modal.service';
import { TranslateService } from '@ngx-translate/core';
import { VolunteeringService } from '../../../../services/volunteering.service';
import { isMobile } from '../../../../utils/screen.utils';
import { Constants } from '../../../../app.constants';
import { VolunteeringConstants } from '../../../../volunteering-module/volunteering/volunteering.constants';
import {
  NsSimpleInfoModalComponent
} from '../../../../shared/modals/ns-simple-info-modal/ns-simple-info-modal.component';
import { ModalConstants } from '../../../../shared/services/modal/modal.constants';
import { finalize } from 'rxjs/operators';


/**
 * Component that asks the user to confirm if they will participate in an external volunteering
 * mission during working hours and persists that decision.
 *
 * Responsibilities:
 *  - Collect the user's choice (yes/no).
 *  - Send the payload to signUpOffer() endpoint.
 *  - Display a success informational modal.
 *
 */
@Component({
  selector: 'app-external-mission-working-hours-confirmation-modal',
  templateUrl: './external-mission-working-hours-confirmation-modal.component.html',
  styleUrls: ['./external-mission-working-hours-confirmation-modal.component.scss']
})
export class ExternalMissionWorkingHoursConfirmationModalComponent {

  /** Reference to the global `isMobile` helper for template binding. */
  readonly isMobile = isMobile;

  /** Constant collections exposed to the template. */
  readonly Constants = Constants;
  readonly VolunteeringConstants = VolunteeringConstants;

  @Input() applicationData!: any;

  isDuringWorkingHours?: boolean;

  constructor(
    private readonly dialogRef: MatDialogRef<ExternalMissionWorkingHoursConfirmationModalComponent>,
    private readonly modal: ModalService,
    @Inject(MAT_DIALOG_DATA) public readonly data: unknown,
    private readonly translate: TranslateService,
    private readonly volunteeringService: VolunteeringService
  ) {
  }

  /**
   * Updates local state from the radio‑button change event.

   */
  onWorkingHoursChange(event: any): void {
    // This way we get a boolean, otherwise the HTML returns a string
    this.isDuringWorkingHours =  (event.target as HTMLInputElement).value === 'true';
  }

  /**
   * Persists the user's decision and shows a feedback modal.
   * The hosting view is optionally refreshed by returning `true` from the dialog.
   */
  onConfirm(): void {
    if (this.isDuringWorkingHours === undefined) {
      this.isDuringWorkingHours = false;
    }

    const payload: any = {
      ...this.applicationData,
      working_hours: this.isDuringWorkingHours
    };

    this.volunteeringService
      .signUpOffer(payload)
      .pipe(finalize(() => this.closeDialog(true)))
      .subscribe({
        next: () => this.openInfoModal(),
      });
  }

  /** Closes the dialog. */
  closeDialog(reloadView = false): void {
    this.dialogRef.close(reloadView);
  }

  /**
   * Opens an informational success modal
   */
  private openInfoModal(): void {
    const modalHeight = isMobile() ? 251 : 272;
    const modalWidth = isMobile() ? 320 : 494;

    const infoModalRef = this.modal.openFixedModal(
      {},
      NsSimpleInfoModalComponent,
      isMobile() ? ModalConstants.PADDING_16_PANEL_CLASS : ModalConstants.DEFAULT_PANEL_CLASS,
      modalWidth,
      modalHeight
    );

    Object.assign(infoModalRef.componentInstance, {
      title: this.translate.instant('EXTERNAL_MISSION.confirmation_title'),
      titleDesktopClasses: 'ns-green',
      infoText: this.translate.instant('EXTERNAL_MISSION.confirmation_text'),
      infoTextDesktopClasses: 'ns-font-16 ns-black ns-line-height-20 ns-mt-24 ns-mb-24',
      buttonText: this.translate.instant('GENERAL.close'),
      buttonType: Constants.BUTTON_TYPE_DEFAULT_YELLOW_FILLED_B
    });
  }
}
